import _cloneDeep from 'lodash/cloneDeep'
import _isEqual from 'lodash/isEqual'
import _orderBy from 'lodash/orderBy'
import _sortBy from 'lodash/sortBy'
import latinize from 'latinize'

import { getCommonNonCompliances, getTaskManagerActionDataset } from "frontend/communications/stats"
import { getFirstDayOfYearDate, getLastDayYearDate, getUTCDatetimeFromDate } from 'frontend/helpers/dateUtils'
import {
  getInterventionModes,
  getTaskManagerAssessors,
  getTaskManagerSuperCategories,
  getTaskManagerTechnicalModules,
  getTaskManagerTypeOfCategories,
} from 'frontend/communications/apiClient'
import { sortAlphabetically } from 'frontcore/utils/helpers/formatters'

const stateObj = {
  categories: [],
  superCategories: [],
  assessors: [],
  common_non_compliances: {},
  number_compliances: {},
  number_non_compliances: {},
  actionDataset: null,
  compliances_by_month: null,
  collapsedCards: [],
  interventionModes: [],
  technicalModules: [],
  taskManagerStatsCache: null,
  taskManagerCachedFilters: null,
}

const getters = {
  taskManagerStats: state => (filters, selectedYear) => {
    filters['created_gte'] = filters.created_gte ?? getUTCDatetimeFromDate(getFirstDayOfYearDate(selectedYear))
    filters['created_lte'] = filters.created_lte ?? getUTCDatetimeFromDate(getLastDayYearDate(selectedYear))
    if (state.taskManagerStatsCache && _isEqual(filters, state.taskManagerCachedFilters) ) {
      return state.taskManagerStatsCache
    }
    state.taskManagerCachedFilters = _cloneDeep(filters)
    state.taskManagerStatsCache = getCommonNonCompliances(filters)
    return state.taskManagerStatsCache
  },
  taskManagerActionStats: () => (filters, selectedYear) => {
    filters['created_gte'] = filters.created_gte ?? getUTCDatetimeFromDate(getFirstDayOfYearDate(selectedYear))
    filters['created_lte'] = filters.created_lte ?? getUTCDatetimeFromDate(getLastDayYearDate(selectedYear))
    return getTaskManagerActionDataset(filters)
  },
  getTaskManagerCategories (state) {
    return state.categories.filter(category => category.enabled)
  },
  getTaskManagerCategoryById: (state) => (id) => {
    return state.categories.find(category => category.id === id)
  },
  getTaskManagerSuperCategories (state) {
    return state.superCategories.filter(superCategory => superCategory.enabled)
  },
  getTaskManagerSuperCategoriesAndException: (state) => (exception) => {
    return state.superCategories.filter(category => {
      return category.enabled || category.id === exception
    }).sort(
      sortAlphabetically('name')
    )
  },
  getTaskManagerSuperCategoryById: (state) => (id) => {
    return state.superCategories.find(superCategory => superCategory.id === id)
  },
  getCategoryById: (state) => (id) => {
    return state.categories.find(category => category.id === id)
  },
  getTaskManagerAssessors (state) {
    return _sortBy(state.assessors, 'order', 'asc')
  },
  getCommonNonCompliances (state) {
    return state.common_non_compliances
  },
  getNumberCompliances (state) {
    return state.number_compliances
  },
  getNumberNonCompliances (state) {
    return state.number_non_compliances
  },
  getTaskManagerActionDataset: (state) => () => {
    return state.actionDataset
  },
  getCompliancesByMonth (state) {
    return state.compliances_by_month
  },
  isCardCollapsed: (state) => (id) => {
    const card = state.collapsedCards.find(c => c.id === id)
    return card ? card.isCollapsed : true
  },
  areAllCardsCollapsed (state) {
    return state.collapsedCards.length === 0
  },
  getInterventionModesWithDisabled (state) {
    return state.interventionModes
  },
  getInterventionModes (state) {
    return state.interventionModes.filter(item => item.enabled)
  },
  getInterventionModeById: (state) => (id) => {
    return state.interventionModes.find(interventionMode => interventionMode.id === id)
  },
  getTechnicalModulesWithDisabled (state) {
    return state.technicalModules
  },
  getTechnicalModules (state) {
    return state.technicalModules.filter(item => item.enabled)
  },
  getTechnicalModuleById: (state) => (id) => {
    return state.technicalModules.find(technicalModule => technicalModule.id === id)
  },
}

const mutations = {
  setTaskManagerCategories (state, categories) {
    state.categories = _orderBy(categories, 'id')
  },
  setTaskManagerSuperCategories (state, superCategories) {
    state.superCategories = _orderBy(superCategories, 'id')
  },
  setTaskManagerCheckpoints (state, checkpoints) {
    state.checkpoints =  _orderBy(checkpoints, checkpoint => latinize(checkpoint.description.toLowerCase()))
  },
  setTaskManagerAssessors (state, assessors) {
    state.assessors = _orderBy(assessors, 'id')
  },
  setCommonNonCompliances (state, common_non_compliances) {
    state.common_non_compliances = common_non_compliances
  },
  setNumberCompliances (state, number_compliances) {
    state.number_compliances = number_compliances
  },
  setNumberNonCompliances (state, number_non_compliances) {
    state.number_non_compliances = number_non_compliances
  },
  setTaskManagerActionDataset (state, dataset) {
    state.actionDataset = dataset
  },
  setNonCompliancesByMonth (state, compliances_by_month) {
    state.compliances_by_month = compliances_by_month
  },
  setCardCollapsed (state, collapsedCard) {
    const card = state.collapsedCards.find(c => c.id === collapsedCard.id)
    if (card) {
      card.isCollapsed = collapsedCard.isCollapsed
    } else {
      state.collapsedCards.push(collapsedCard)
    }
  },
  setAllCardsCollapsed (state) {
    state.collapsedCards = []
  },
  updateTaskManagerCategory (state, value) {
    const categoryIndex = state.categories.findIndex(category => category.id === value.id)
    if (categoryIndex !== -1) {
      state.categories.splice(categoryIndex, 1, value)
    }
  },
  updateTaskManagerSuperCategory (state, value) {
    const superCategoryIndex = state.superCategories.findIndex(superCategory => superCategory.id === value.id)
    if (superCategoryIndex !== -1) {
      state.superCategories.splice(superCategoryIndex, 1, value)
    }
  },
  setInterventionModes (state, interventionModes) {
    state.interventionModes = _orderBy(interventionModes, 'id')
  },
  updateTaskManagerInterventionMode (state, value) {
    const interventionModeIndex = state.interventionModes.findIndex(interventionMode => interventionMode.id === value.id)
    if (interventionModeIndex !== -1) {
      state.interventionModes.splice(interventionModeIndex, 1, value)
    }
  },
  setTechnicalModules (state, technicalModules) {
    state.technicalModules = _orderBy(technicalModules, 'id')
  },
  updateTaskManagerTechnicalModule (state, value) {
    const technicalModuleIndex = state.technicalModules.findIndex(technicalModule => technicalModule.id === value.id)
    if (technicalModuleIndex !== -1) {
      state.technicalModules.splice(technicalModuleIndex, 1, value)
    }
  },
}

const actions = {
  async fetchTaskManagerCategories ({ commit }) {
    const categories = await getTaskManagerTypeOfCategories()
    commit('setTaskManagerCategories', categories)
    return categories
  },
  async fetchTaskManagerSuperCategories ({ commit }) {
    const superCategories = await getTaskManagerSuperCategories()
    commit('setTaskManagerSuperCategories', superCategories)
    return superCategories
  },
  async fetchTaskManagerAssessors ({ commit }) {
    const assessors = await getTaskManagerAssessors()
    commit('setTaskManagerAssessors', assessors)
    return assessors
  },
  async fetchCommonNonCompliances ({ commit }, payload) {
    const data = await getCommonNonCompliances(payload)
    commit('setCommonNonCompliances', data.common_non_compliances)
    commit('setNumberCompliances', data.number_compliances)
    commit('setNumberNonCompliances', data.number_non_compliances)
    commit('setNonCompliancesByMonth', data.compliances_by_month)
    return data
  },
  async fetchTaskManagerActionDataset ({ commit }, payload) {
    const dataset = await getTaskManagerActionDataset(payload)
    commit('setTaskManagerActionDataset', dataset)
    return dataset
  },
  async fetchInterventionModes ({ commit }) {
    const data = await getInterventionModes()
    commit('setInterventionModes', data)
    return data
  },
  async fetchTechnicalModules ({ commit }) {
    const data = await getTaskManagerTechnicalModules()
    commit('setTechnicalModules', data)
    return data
  },
}

export default {
  actions,
  getters,
  mutations,
  state: stateObj,
}
